import React, { useEffect, useRef, useState } from 'react'
import { makeStyles, Button, Grid, InputField, useTranslation } from '@paintscout/ui'
import {
  TimeSelect,
  DealSelect,
  EstimatorSelect,
  SingleDatePicker,
  Editor,
  useDialogs,
  useUsers
} from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import type { Deal, Task } from '@paintscout/api'
import { useDealQuery } from '@paintscout/api'
import type { DashboardEstimator } from '@paintscout/util/builder'
import { addAuth0Prefix, removeAuth0Prefix } from '@paintscout/util'
import { useConnection } from 'react-detect-offline'
import type { Moment } from 'moment'
import moment from 'moment'
import AddIcon from '@material-ui/icons/Add'
import { goToDeal } from 'shared/util/routing/goToDeal'
import classNames from 'classnames'
import { useTaskValues } from 'shared/components/forms/useTaskValues'
import { canAssignTaskToUser } from 'shared/components/forms/canAssignTaskToUser'
import { TASK_TITLE_PATTERN } from './taskTitlePattern'

export { canAssignTaskToUser }
export { TASK_TITLE_PATTERN }

type FormProps = JSX.IntrinsicElements['form']

export interface TaskFormProps extends Omit<FormProps, 'onSubmit'> {
  classes?: StyleClasses<typeof useStyles>
  task?: Partial<Task>
  disabled?: boolean
  disableCompanySelector?: boolean
  isSecondary?: boolean
  hideDeal?: boolean
  hideUser?: boolean
  defaultEstimator?: string
  onSubmit: (task: Task) => any
  dealQuery?: string // lucene query for deals
  searchCompanyId?: string // specify company id to search deals for
  isNew?: boolean
  onDialogBack?: (event: React.MouseEvent<HTMLElement>) => void
  onDialogCancel?: (event: React.MouseEvent<HTMLElement>) => void
  onEdit?: (task: Task) => void
  /** Optional DealDialog component to open when creating a new deal from the task form.
   * When not provided, the "New" deal button will be hidden. */
  DealDialogComponent?: React.ComponentType<any>
}

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    selectField: { marginTop: theme.spacing() },
    estimatorSelect: {
      width: '100%'
    },
    editButton: {
      alignSelf: 'start',
      marginTop: 20,
      [theme.breakpoints.down('md')]: {
        marginTop: 22
      }
    },
    disabledButton: {
      outline: `1px solid ${theme.paintscout.palette.brand.Neutral.P80}`
    },
    textField: {
      color: theme.palette.text.primary
    },
    error: {
      position: 'absolute'
    },
    editorDiv: {
      marginBottom: theme.spacing(3)
    },
    datePickerDialogContent: {
      display: 'flex',
      justifyContent: 'center'
    }
  }),
  { name: 'TaskForm' }
)

function TaskForm({
  task,
  disabled,
  onSubmit,
  hideDeal,
  hideUser,
  defaultEstimator,
  searchCompanyId,
  dealQuery,
  isNew,
  onDialogBack,
  onDialogCancel,
  onEdit,
  DealDialogComponent,
  ...baseProps
}: TaskFormProps) {
  const classes = useStyles({})
  const { __ } = useTranslation()
  const { online } = useConnection()
  const {
    handleDateChange,
    handleDealChange,
    handleFieldChange,
    handleOwnerChange,
    handleTimeChange,
    handleNotesChange,
    setTaskValues,
    taskValues
  } = useTaskValues({ task, defaultEstimator })
  const { usersById } = useUsers()
  const [titleTooLong, setTitleTooLong] = useState(false)
  const [dealOpen, setDealOpen] = useState(false)
  const dealOpenRef = useRef(false)
  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()
  const { data: dealData } = useDealQuery({
    variables: { id: taskValues?.dealId },
    skip: !taskValues?.dealId,
    fetchPolicy: 'cache-first'
  })
  const dealAssignedUsers = dealData?.deal?.assignedUsers as Record<string, { assigned?: boolean }> | undefined
  const dealOwnerId = dealData?.deal?.owner?.id

  async function handleSubmit(event: React.FormEvent<HTMLElement>) {
    event.preventDefault()

    // merge taskValues and omit falsey taskValues
    const newTask: Task = {
      ...taskValues,
      updated: Date.now()
    }

    Object.keys(newTask).forEach((key) => {
      if (typeof newTask[key] !== 'string') {
        return
      }

      newTask[key] = newTask[key].trim()
    })

    onSubmit(newTask)
  }
  const commonInputProps = {
    onChange: handleFieldChange,
    disabled,
    fullWidth: true,
    classes: { root: classes.textField }
  }

  useEffect(() => {
    onEdit?.(taskValues)
  }, [taskValues])

  // The default-seeded owner (useTaskValues.getValues) only gets an id, no name. This backfills
  // the name from the shared user list directly, rather than relying on EstimatorSelect's
  // onFetched — that callback only fires once per session (guarded by a users-list-changed ref
  // in UsersProvider/EstimatorSelect), so it never runs for a task created later in the same
  // session once the user list has already loaded, leaving the name permanently blank on save.
  useEffect(() => {
    if (!defaultEstimator || !taskValues?.owner?.id || taskValues.owner.firstName) return
    if (addAuth0Prefix(taskValues.owner.id) !== addAuth0Prefix(defaultEstimator)) return
    const user = usersById?.[removeAuth0Prefix(defaultEstimator)]
    if (!user) return
    handleOwnerChange({ value: addAuth0Prefix(defaultEstimator), label: user.name })
  }, [defaultEstimator, taskValues?.owner?.id, taskValues?.owner?.firstName, usersById, handleOwnerChange])

  const handleDealButtonPress = (event: React.SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (!DealDialogComponent) return
    const shouldCreateNew = dealOpenRef.current || !taskValues.dealId
    openDialog(DealDialogComponent, {
      dealId: shouldCreateNew ? null : taskValues.dealId,
      onConfirm: (_deal: Deal) => {
        setTaskValues({
          ...taskValues,
          dealId: _deal._id,
          dealName: _deal.name
        })
        dismissDialog()
      },
      onBack: onDialogBack ?? dismissDialog,
      onCancel: onDialogCancel ?? dismissAllDialogs
    })
  }

  return (
    <form className={classes.root} autoComplete="off" onSubmit={handleSubmit} {...baseProps}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={hideDeal ? 6 : 12}>
          <InputField
            {...commonInputProps}
            className="tour-task-form-title-field"
            required={true}
            error={!taskValues.title || taskValues?.title?.length > 50}
            pattern={{ expression: TASK_TITLE_PATTERN, title: __('Name') }}
            id="title"
            label={__('Title')}
            autoComplete={'off'}
            value={taskValues.title}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const value = event.target.value
              setTitleTooLong(value.length > 50)
              handleFieldChange(event)
            }}
            helperText={titleTooLong ? __('Task title cannot be longer than 50 characters.') : ''}
            showErrorBeforeValidation={taskValues?.title?.length > 50}
          />
        </Grid>
        {!hideDeal && (
          <Grid item xs={12} md={6}>
            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '18px', alignItems: 'center' }}>
              <div style={{ width: '100%' }}>
                <DealSelect
                  className="tour-task-form-deal-field"
                  query={dealQuery || ''}
                  name="dealId"
                  label={__('Deal')}
                  fullWidth
                  variant="single"
                  required
                  nameValue={taskValues?.dealName || ''}
                  error={!taskValues?.dealId}
                  searchable
                  searchByName
                  popperModifiers={{
                    flip: {
                      enabled: false
                    },
                    preventOverflow: {
                      enabled: false
                    }
                  }}
                  searchCompanyId={searchCompanyId}
                  disabled={disabled || !online || !isNew}
                  hideIcon={!isNew}
                  value={taskValues?.dealId ?? null}
                  onChange={(dealOption: DropdownSelectOption) => {
                    handleDealChange(dealOption)
                  }}
                  onFocus={() => {
                    setDealOpen(true)
                    dealOpenRef.current = true
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setDealOpen(false)
                      dealOpenRef.current = false
                    }, 0)
                  }}
                />
              </div>
              {isNew ? (
                <>
                  {DealDialogComponent && (
                    <Button
                      variant="dialog"
                      color="primary"
                      inputHeight
                      className={classes.editButton}
                      startIcon={!taskValues.dealId || dealOpen ? <AddIcon /> : null}
                      onMouseDown={handleDealButtonPress}
                      onTouchStart={handleDealButtonPress}
                      onPointerDown={handleDealButtonPress}
                      onClick={handleDealButtonPress}
                    >
                      {taskValues.dealId && !dealOpen ? __('Edit') : __('New')}
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  variant="dialog"
                  color="primary"
                  inputHeight
                  className={classes.editButton}
                  startIcon={null}
                  onClick={() => {
                    goToDeal(taskValues.dealId)
                  }}
                >
                  {__('View Deal')}
                </Button>
              )}
            </div>
          </Grid>
        )}
        {!hideUser && (
          <Grid item xs={12} md={6}>
            <EstimatorSelect
              key={taskValues?.dealId ?? 'no-deal'}
              name="estimators"
              label={__('User')}
              className={classNames(classes.estimatorSelect, 'tour-task-form-owner-field')}
              fullWidth
              variant="single"
              searchable
              value={taskValues?.owner?.id ? addAuth0Prefix(taskValues.owner.id) : null}
              disabled={disabled || !online || (!hideDeal && !taskValues?.dealId)}
              includeUser={(user) => canAssignTaskToUser(user, dealAssignedUsers, dealOwnerId)}
              onChange={async (estimatorOption: DashboardEstimator) => {
                handleOwnerChange(estimatorOption)
              }}
              onFetched={(estimatorOptions: DashboardEstimator[]) => {
                // Owner-name backfill for the default-seeded case lives in the effect above,
                // independent of this callback (see its comment for why).
                if (taskValues?.owner?.id) return
                if (defaultEstimator) {
                  const fullEstimator = estimatorOptions.find(
                    (estimator) => estimator.value === addAuth0Prefix(defaultEstimator)
                  )
                  if (fullEstimator) {
                    handleOwnerChange(fullEstimator)
                  }
                } else if (estimatorOptions.length === 1) {
                  handleOwnerChange(estimatorOptions[0])
                }
              }}
              hasUnselect
              autoApply
            />
          </Grid>
        )}
        <Grid item xs={12} className={classes.editorDiv}>
          <Editor
            classes={{ root: 'tour-task-form-description-field' }}
            disabled={disabled}
            label={__('Description')}
            content={taskValues?.notes}
            fullWidth={true}
            debounce={400}
            onChange={(value) => {
              handleNotesChange(value)
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} className="tour-task-form-due-date-field">
          <SingleDatePicker
            classes={{ dialogContent: classes.datePickerDialogContent }}
            label={__('Due Date')}
            date={taskValues?.startDateTime ? moment(taskValues.startDateTime) : null}
            disabled={disabled}
            clearable
            onChange={(newDate: Moment) => {
              handleDateChange(newDate ? newDate.toDate() : null)
            }}
            PopperProps={{
              placement: 'bottom-end',
              modifiers: {
                preventOverflow: {
                  enabled: false
                }
              }
            }}
            modalBreakpoint={'xl'}
          />
        </Grid>
        <Grid item xs={12} md={6} className="tour-task-form-due-time-field">
          <TimeSelect
            onChange={(time: DropdownSelectOption) => {
              handleTimeChange(time.value)
            }}
            value={taskValues.startDateTime}
            variant={'single'}
            label={__('Time')}
            disabled={disabled || !taskValues.startDateTime}
            startAt={6}
          />
        </Grid>
      </Grid>
    </form>
  )
}

export default TaskForm
